<template>
  <div class="checkpoint-status-menu">
    <div class="checkpoint-status-menu__tabs">
      <HorizontalMenu
        :key="menuKey"
        :items="menuItems"
        :value="menuTabValue"
        size="small"
        :bottom-line="false"
        :hide-lines="true"
        @onChange="handleTabChange"
      />
    </div>
    <div class="checkpoint-status-menu__actions">
      <template v-if="showActivate">
        <StandardButton
          type="magenta"
          variant="primary"
          size="small"
          label="Activate"
          :disabled="activateDisabled"
          @click="emit('activate')"
        />
      </template>
      <template v-else-if="showPropose">
        <StandardButton
          type="magenta"
          variant="primary"
          size="small"
          label="Propose Changes"
          @click="onProposeChangesClick"
        />
      </template>
      <template v-else-if="showPublishDiscard">
        <StandardButton
          type="magenta"
          variant="primary"
          size="small"
          label="Publish"
          :disabled="publishDisabled"
          @click="emit('publish')"
        />
        <StandardButton
          type="magenta"
          variant="outline"
          size="small"
          label="Discard"
          @click="emit('discard')"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  state: {
    type: String,
    required: true,
    validator: (value) =>
      [
        "draft_activate_enabled",
        "draft_activate_disabled",
        "active_propose",
        "proposed_viewing_active",
        "proposed_drafting",
        "proposed_publish_disabled",
      ].includes(value),
  },
  proposedTabIndex: {
    type: Number,
    default: null,
  },
  activateDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["activate", "proposeChanges", "publish", "discard", "tabChange"]);

const isDualTab = computed(() =>
  ["proposed_viewing_active", "proposed_drafting", "proposed_publish_disabled"].includes(
    props.state
  )
);

const menuKey = computed(() => `${props.state}-${isDualTab.value ? "dual" : "single"}`);

/** HorizontalMenu only hides the count badge when `count` is strictly `null`. */
function tabItem(title) {
  return { title, count: null };
}

const menuItems = computed(() => {
  if (isDualTab.value) {
    return [tabItem("Active Checkpoint"), tabItem("Proposed Changes")];
  }
  if (props.state === "active_propose") {
    return [tabItem("Active Checkpoint")];
  }
  return [tabItem("Draft Checkpoint")];
});

const defaultTabForState = computed(() => {
  if (!isDualTab.value) return 0;
  switch (props.state) {
    case "proposed_viewing_active":
      return 0;
    case "proposed_drafting":
    case "proposed_publish_disabled":
      return 1;
    default:
      return 0;
  }
});

const menuTabValue = computed(() => {
  if (props.proposedTabIndex !== null && props.proposedTabIndex !== undefined) {
    return props.proposedTabIndex;
  }
  return defaultTabForState.value;
});
const showActivate = computed(
  () => props.state === "draft_activate_enabled" || props.state === "draft_activate_disabled"
);

const showPropose = computed(() => props.state === "active_propose");

const showPublishDiscard = computed(
  () => props.state === "proposed_drafting" || props.state === "proposed_publish_disabled"
);

const publishDisabled = computed(() => props.state === "proposed_publish_disabled");

function handleTabChange(index) {
  emit("tabChange", index);
}

function onProposeChangesClick() {
  return false;
  emit("proposeChanges");
}
</script>

<style scoped>
.checkpoint-status-menu {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  gap: 16px;
  /* No horizontal inset — edge-to-edge. Vertical space only (top + above border + below bar). */
  padding: 12px 0;
  margin-bottom: 12px;
  border-bottom: 1px solid #eaecf0;
}

.checkpoint-status-menu__tabs {
  flex: 1;
  min-width: 0;
}

.checkpoint-status-menu__actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  padding-bottom: 2px;
}

/* Thinner active tab underline than default HorizontalMenu (3px). */
.checkpoint-status-menu :deep(.horizontal-tab .v-tabs-slider-wrapper) {
  height: 2px !important;
}
</style>
